import { portalPaths } from '@provision/portal-routes';
import { useEffect, useRef } from 'react';
import { doNothing } from 'remeda';
import { useNotificationNavigation } from 'src/components/Navbar/DesktopDrawer/InboxPane/hooks/use-notification-navigation';
import { NotificationStatus, useNotificationRecords } from 'src/graphql/entities/notification';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import useNavigationStore from 'src/store/navigation';
import { useNavigateRoute } from 'src/utils/routing';
import { InboxClearState } from './inbox-clear-state';

const DEFAULT_LANDING_PATH = portalPaths.entity.all({ entityName: 'conversations' });

// the pane's own list query minus its Type filter, so the cache is shared and
// "truly clear" means the whole inbox, not the filtered view
const NON_ARCHIVED_FILTERS = [
  { attribute: 'status', operator: '!=', value: NotificationStatus.Archived },
];

// bare /inbox: with notifications left it reopens the inbox pane over the
// conversations grid, which owns the list; with none left it is the clear state
export const InboxLanding = () => {
  const { navigate } = useNavigateRoute();
  const { user } = useCurrentUserProfile();
  const { notifications, isLoading, error } = useNotificationRecords({
    filters: NON_ARCHIVED_FILTERS,
  });
  const { navigateToTarget } = useNotificationNavigation({ markOneAsRead: doNothing });

  // the list query is skipped until the user profile is in, and a skipped query
  // reports an empty, not-loading list — not a clear inbox
  const isPending = isLoading || !user?.id;
  const isClear = !isPending && !error && notifications.length === 0;
  const wasClearRef = useRef(false);

  useEffect(() => {
    if (isClear) wasClearRef.current = true;
  }, [isClear]);

  // the celebration never sits over a non-empty inbox: a notification that
  // arrives while it is up opens; landing here with items redirects as before
  useEffect(() => {
    if (isPending || isClear) return;

    const [first] = notifications;
    if (wasClearRef.current && first) {
      navigateToTarget(first);
      return;
    }

    useNavigationStore.getState().setPendingPane('inbox');
    navigate(DEFAULT_LANDING_PATH, { replace: true });
  }, [isPending, isClear, notifications, navigate, navigateToTarget]);

  // no exit control: switching to any other nav tab leaves the clear state.
  // Subscribed rather than selected, so the mount-time pane switch below
  // can't read as the user leaving
  useEffect(() => {
    useNavigationStore.getState().setActivePane('inbox');

    return useNavigationStore.subscribe((state, previous) => {
      if (state.activePane === previous.activePane || state.activePane === 'inbox') return;
      state.setPendingPane(state.activePane);
      navigate(DEFAULT_LANDING_PATH, { replace: true });
    });
  }, [navigate]);

  return isClear ? <InboxClearState /> : null;
};
